import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Inbox, Loader2, Undo2 } from 'lucide-react';
import { GlassModal } from '../ui/GlassModal';
import { GlassButton } from '../ui/GlassButton';
import {
  assignCrmUnassigned,
  CrmProjectEntry,
  CrmUnassignedModule,
  CrmUnassignedRecord,
  getCrmUnassigned,
  undoCrmUnassigned,
} from '../../services/api';
import './CrmUnassignedPanel.css';

/**
 * crm-per-project-scoping: owner triage of CRM records in their Unassigned bucket.
 * Nothing is assigned to a project without the owner's explicit choice.
 */
export interface CrmUnassignedPanelProps {
  isOpen: boolean;
  onClose: () => void;
  /** Projects from GET /api/crm/projects; only the caller's OWN projects are assign targets. */
  projects: CrmProjectEntry[];
  /** Called after a successful assign/undo so the CRM view can refresh. */
  onChanged?: () => void;
}

const TABS: CrmUnassignedModule[] = ['Leads', 'Contacts', 'Accounts'];
const PER_PAGE = 50;

const errDetail = (e: any, fallback: string): string => {
  if (e?.response?.status === 429) return 'Too many requests — please wait a minute and try again.';
  const d = e?.response?.data?.detail;
  return typeof d === 'string' ? d : fallback;
};

const CrmUnassignedPanel: React.FC<CrmUnassignedPanelProps> = ({ isOpen, onClose, projects, onChanged }) => {
  const ownProjects = useMemo(() => projects.filter(p => p.is_owner && p.crm_ready), [projects]);
  const [tab, setTab] = useState<CrmUnassignedModule>('Leads');
  const [records, setRecords] = useState<CrmUnassignedRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [rowProject, setRowProject] = useState<Record<string, string>>({});
  const [defaultProject, setDefaultProject] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<{ text: string; batchIds: string[] } | null>(null);

  useEffect(() => {
    if (!defaultProject && ownProjects.length > 0) setDefaultProject(ownProjects[0].project_id);
  }, [ownProjects, defaultProject]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getCrmUnassigned(tab, 1, PER_PAGE);
      setRecords(data.records || []);
      setNotice(data.crm_scope === 'none' ? (data.notice || "Your unassigned CRM records aren't available right now.") : null);
    } catch (e: any) {
      setRecords([]);
      setError(errDetail(e, 'Could not load unassigned records.'));
    } finally {
      setLoading(false);
      setSelected({});
    }
  }, [tab]);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  const projectName = (pid: string) => ownProjects.find(p => p.project_id === pid)?.name || 'the project';

  const selectedIds = records.filter(r => selected[r.id]).map(r => r.id);

  const handleAssignSelected = async () => {
    if (selectedIds.length === 0) return;
    // Group by the chosen project (per-row select, else the default).
    const groups: Record<string, Array<{ module: CrmUnassignedModule; id: string }>> = {};
    for (const r of records) {
      if (!selected[r.id]) continue;
      const pid = rowProject[r.id] || defaultProject;
      if (!pid) continue;
      (groups[pid] = groups[pid] || []).push({ module: r.module || tab, id: r.id });
    }
    setBusy(true);
    setError(null);
    let moved = 0; let rejected = 0; let failed = 0;
    const batchIds: string[] = [];
    let groupError: string | null = null;
    // One request per chosen project. A later group failing must NOT hide what the earlier
    // groups already moved, nor lose their undo batches.
    for (const [pid, recs] of Object.entries(groups)) {
      try {
        const res = await assignCrmUnassigned({ project_id: pid, records: recs });
        moved += res.moved; rejected += res.rejected; failed += res.failed;
        if (res.batch_id) batchIds.push(res.batch_id);
      } catch (e: any) {
        groupError = errDetail(e, 'Could not assign the selected records.');
        break;
      }
    }
    if (moved > 0 || rejected > 0 || failed > 0 || batchIds.length > 0) {
      setToast({
        text: `Moved ${moved} record${moved === 1 ? '' : 's'}${rejected ? `, ${rejected} not in your bucket` : ''}${failed ? `, ${failed} failed` : ''}.`,
        batchIds,
      });
      onChanged?.();
    }
    if (groupError) setError(moved > 0 ? `${groupError} (${moved} record${moved === 1 ? ' was' : 's were'} moved before the error.)` : groupError);
    try {
      await load();
    } finally {
      setBusy(false);
    }
  };

  const handleAssignAll = async () => {
    if (!defaultProject) return;
    const n = `${records.length}${records.length >= PER_PAGE ? '+' : ''}`;
    const name = projectName(defaultProject);
    if (!window.confirm(`Move all ${n} records into ${name}? Members of ${name} will be able to see them.`)) return;
    setBusy(true);
    setError(null);
    try {
      const res = await assignCrmUnassigned({ project_id: defaultProject, all: true, module: tab });
      setToast({
        text: `Moved ${res.moved} record${res.moved === 1 ? '' : 's'} into ${name}${res.failed ? ` (${res.failed} failed)` : ''}.${res.truncated ? ' More records remain: run it again to continue.' : ''}`,
        batchIds: res.batch_id ? [res.batch_id] : [],
      });
      onChanged?.();
      await load();
    } catch (e: any) {
      setError(errDetail(e, 'Could not assign the records.'));
    } finally {
      setBusy(false);
    }
  };

  const handleUndo = async () => {
    if (!toast || toast.batchIds.length === 0) return;
    setBusy(true);
    let reverted = 0;
    let undoError: string | null = null;
    for (const id of toast.batchIds) {
      try {
        const res = await undoCrmUnassigned(id);
        reverted += res.reverted;
      } catch (e: any) {
        undoError = errDetail(e, 'Could not undo that change.');
        break;
      }
    }
    setToast({ text: `Undone: ${reverted} record${reverted === 1 ? '' : 's'} moved back.`, batchIds: [] });
    if (undoError) setError(undoError);
    onChanged?.();
    try {
      await load();
    } finally {
      setBusy(false);
    }
  };

  const allChecked = records.length > 0 && records.every(r => selected[r.id]);

  return (
    <GlassModal
      isOpen={isOpen}
      onClose={onClose}
      title="Unassigned CRM records"
      size="xl"
      footer={
        <div className="crm-unassigned__footer">
          <GlassButton variant="secondary" onClick={onClose}>Close</GlassButton>
          <GlassButton
            variant="secondary"
            onClick={handleAssignAll}
            disabled={busy || !defaultProject || records.length === 0}
          >
            Assign all in this tab to…
          </GlassButton>
          <GlassButton
            variant="primary"
            onClick={handleAssignSelected}
            disabled={busy || selectedIds.length === 0 || (!defaultProject && selectedIds.some(id => !rowProject[id]))}
            loading={busy}
          >
            Assign selected
          </GlassButton>
        </div>
      }
    >
      <div className="crm-unassigned">
        <p className="crm-unassigned__intro">
          These CRM records aren't assigned to a project yet. Choose a project for each, or move a whole tab at once.
          Members of a project can see its records.
        </p>

        <div className="crm-unassigned__tabs" role="tablist">
          {TABS.map(t => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={t === tab}
              className={`crm-unassigned__tab${t === tab ? ' is-active' : ''}`}
              onClick={() => setTab(t)}
            >
              {t}
            </button>
          ))}
        </div>

        <label className="crm-unassigned__default">
          <span>Project</span>
          <select value={defaultProject} onChange={e => setDefaultProject(e.target.value)} disabled={ownProjects.length === 0}>
            {ownProjects.length === 0 && <option value="">You don't own a project with CRM ready</option>}
            {ownProjects.map(p => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
          </select>
        </label>

        {error && <div className="crm-unassigned__error" role="alert">{error}</div>}
        {notice && <div className="crm-unassigned__notice" role="status">{notice}</div>}

        {loading ? (
          <div className="crm-unassigned__loading"><Loader2 size={16} className="crm-unassigned__spin" /> Loading…</div>
        ) : records.length === 0 && !notice ? (
          <div className="crm-unassigned__empty"><Inbox size={20} /> No unassigned {tab.toLowerCase()}.</div>
        ) : records.length > 0 ? (
          <div className="crm-unassigned__table-wrap">
            <table className="crm-unassigned__table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      aria-label="Select all"
                      checked={allChecked}
                      onChange={e => {
                        const v = e.target.checked;
                        const next: Record<string, boolean> = {};
                        records.forEach(r => { next[r.id] = v; });
                        setSelected(next);
                      }}
                    />
                  </th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Company</th>
                  <th>Created</th>
                  <th>Project</th>
                </tr>
              </thead>
              <tbody>
                {records.map(r => (
                  <tr key={r.id}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Select ${r.name || r.id}`}
                        checked={!!selected[r.id]}
                        onChange={e => setSelected(prev => ({ ...prev, [r.id]: e.target.checked }))}
                      />
                    </td>
                    <td>{r.name || '—'}</td>
                    <td>{r.email1 || '—'}</td>
                    <td>{r.account_name || '—'}</td>
                    <td>{r.date_entered ? new Date(r.date_entered.replace(' ', 'T')).toLocaleDateString() : '—'}</td>
                    <td>
                      <select
                        value={rowProject[r.id] || ''}
                        onChange={e => setRowProject(prev => ({ ...prev, [r.id]: e.target.value }))}
                        aria-label="Project"
                      >
                        <option value="">{defaultProject ? `Default (${projectName(defaultProject)})` : 'Choose…'}</option>
                        {ownProjects.map(p => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {toast && (
          <div className="crm-unassigned__toast" role="status">
            <span>{toast.text}</span>
            {toast.batchIds.length > 0 && (
              <GlassButton variant="secondary" size="sm" icon={<Undo2 size={14} />} onClick={handleUndo} disabled={busy}>
                Undo
              </GlassButton>
            )}
          </div>
        )}
      </div>
    </GlassModal>
  );
};

export default CrmUnassignedPanel;
